import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HandsetModel, ProvisioningPreview, ProvisioningRoleInfo, ProvisioningVendor,
} from '@arcnet/contracts';
import { api } from './client.js';

/**
 * The provisioning section's own data. Kept apart from the device hooks because
 * one of these reads a credential and the other is a catalogue.
 */

/** Which makes get a generated file, read from the registry rather than listed here. */
export const useProvisioningVendors = () =>
  useQuery({
    queryKey: ['provisioning-vendors'] as const,
    queryFn: () => api.get<{ data: ProvisioningVendor[] }>('/v1/provisioning/vendors'),
    // A build-time fact: the set changes when we ship a vendor, not hourly.
    staleTime: 60 * 60_000,
  });

/**
 * What a handset would be served.
 *
 * A mutation, not a query, on purpose: every fetch writes an audit entry, and
 * the revealed form contains a SIP password. Neither belongs to anything that
 * refetches on a timer or when a window regains focus.
 */
export function usePreviewConfig(tenantId: string) {
  return useMutation({
    mutationFn: ({ deviceId, reveal }: { deviceId: string; reveal?: boolean }) =>
      api.get<ProvisioningPreview>(
        `/v1/tenants/${tenantId}/devices/${deviceId}/provisioning/preview${reveal ? '?reveal=true' : ''}`),
  });
}

/** The supported-handset catalogue (docs/26 C1): what a handset can be. */
export const useHandsetCatalogue = () =>
  useQuery({
    queryKey: ['provisioning-handsets'] as const,
    queryFn: () => api.get<{ data: HandsetModel[] }>('/v1/provisioning/handsets'),
    staleTime: 5 * 60_000,
  });

/** The provisioning roles (docs/26 C2), read from the module's templates. */
export const useProvisioningRoles = () =>
  useQuery({
    queryKey: ['provisioning-roles'] as const,
    queryFn: () => api.get<{ data: ProvisioningRoleInfo[] }>('/v1/provisioning/roles'),
    // Changes when a role ships, not while anybody is looking.
    staleTime: 60 * 60_000,
  });

export function useSaveHandsetModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch(`/v1/provisioning/handsets/${encodeURIComponent(id)}`, body)
         : api.post<{ id: string }>('/v1/provisioning/handsets', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['provisioning-handsets'] }); },
  });
}
